import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { leadsApi } from "@/api/leads";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Props = {
  from: string;
  to: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  hospitalId?: string;
};

export function LeadBantScoringTab({
  from,
  to,
  onFromChange,
  onToChange,
  hospitalId,
}: Props) {
  const [source, setSource] = useState("");
  const [campaign, setCampaign] = useState("");

  const filterOptionsQ = useQuery({
    queryKey: ["lead-filter-options-bant", hospitalId ?? "all"],
      queryFn: async () => leadsApi.getLeadFilterOptions(hospitalId ? { hospitalId } : undefined),
    });

  const reportParams = useMemo(() => {
    const p: {
      from: string;
      to: string;
      source?: string;
      campaignName?: string;
      hospitalId?: string;
    } = { from, to };
    if (source) p.source = source;
    if (campaign) p.campaignName = campaign;
    if (hospitalId) p.hospitalId = hospitalId;
    return p;
  }, [from, to, source, campaign, hospitalId]);

  const reportQ = useQuery({
    queryKey: ["lead-bant-scoring-report", reportParams],
    queryFn: async () => (await leadsApi.getBantScoringReport(reportParams)).data.data,
    enabled: !!from && !!to,
  });

  const data = reportQ.data;
  const sources: string[] = filterOptionsQ.data?.sources ?? [];
  const campaigns: string[] = filterOptionsQ.data?.campaignNames ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Lead Scoring Performance</CardTitle>
          <p className="text-sm text-muted-foreground">
            Period uses <code className="text-xs bg-muted px-1 rounded">scoreLastUpdatedAt</code>{" "}
            (when BDAs last saved BANT scores). Conversion = % reaching OPD Visited.
          </p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">From</Label>
            <Input type="date" value={from} onChange={(e) => onFromChange(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">To</Label>
            <Input type="date" value={to} onChange={(e) => onToChange(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Source</Label>
            <Select
              value={source || "__all__"}
              onValueChange={(v) => setSource(v === "__all__" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All sources</SelectItem>
                {sources.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Campaign</Label>
            <Select
              value={campaign || "__all__"}
              onValueChange={(v) => setCampaign(v === "__all__" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All campaigns" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All campaigns</SelectItem>
                {campaigns.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {reportQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data ? (
        <p className="text-sm text-muted-foreground">No data.</p>
      ) : (
        <>
          <div className="text-sm text-muted-foreground">
            Leads with a score update in range:{" "}
            <strong className="text-foreground">{data.totalScoredInPeriod ?? 0}</strong>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">A — Tier distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto border rounded-md">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-muted-foreground">
                    <tr>
                      <th className="p-2 text-left">Tier</th>
                      <th className="p-2 text-right">Count</th>
                      <th className="p-2 text-right">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.tierDistribution || []).map((row: any) => (
                      <tr key={row.tier} className="border-t">
                        <td className="p-2 font-medium">{row.tier}</td>
                        <td className="p-2 text-right tabular-nums">{row.count}</td>
                        <td className="p-2 text-right tabular-nums">{row.percent}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">B — Tier conversion</CardTitle>
              <p className="text-xs text-muted-foreground">
                Accuracy check: Hot should convert (OPD Visited %) higher than Warm → Cold → Bad.
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto border rounded-md">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-muted-foreground">
                    <tr>
                      <th className="p-2 text-left">Tier</th>
                      <th className="p-2 text-right">Total Leads</th>
                      <th className="p-2 text-right">% OPD Booked</th>
                      <th className="p-2 text-right">% OPD Visited (= Conversion)</th>
                      <th className="p-2 text-right">% IPD Done</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.conversion || []).map((row: any) => (
                      <tr key={row.tier} className="border-t">
                        <td className="p-2 font-medium">{row.tier}</td>
                        <td className="p-2 text-right tabular-nums">{row.totalLeads}</td>
                        <td className="p-2 text-right tabular-nums">{row.pctOpdBooked}%</td>
                        <td className="p-2 text-right tabular-nums font-semibold">
                          {row.pctOpdVisited}%
                        </td>
                        <td className="p-2 text-right tabular-nums">{row.pctIpdDone}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">C — Data quality</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-md border p-4">
                <div className="text-xs text-muted-foreground">
                  Budget = &quot;Won&apos;t Reveal&quot; (flat 3)
                </div>
                <div className="text-2xl font-semibold mt-1 tabular-nums">
                  {data.dataQuality?.pctBudgetWontReveal ?? 0}%
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {data.dataQuality?.budgetWontRevealCount ?? 0} of{" "}
                  {data.totalScoredInPeriod ?? 0} scored leads
                </div>
              </div>
              <div className="rounded-md border p-4">
                <div className="text-xs text-muted-foreground">Profile Incomplete</div>
                <div className="text-2xl font-semibold mt-1 tabular-nums">
                  {data.dataQuality?.pctProfileIncomplete ?? 0}%
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {data.dataQuality?.profileIncompleteCount ?? 0} of{" "}
                  {data.totalScoredInPeriod ?? 0} scored leads
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
